import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Typography,
  Box,
  TextField,
  Stack,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import {
  Close,
  PhotoCamera,
  Palette,
  Logout,
  DeleteForever
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { updateProfile as updateAuthProfile } from 'firebase/auth';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import ProfileCustomization from './ProfileCustomization';

function UserProfile({
  open,
  onClose,
  userProfile,
  onUploadProfilePhoto,
  onShowToast,
  onLogout
}) {
  const { currentUser, fetchUserProfile, deleteAccount } = useAuth();
  const [displayName, setDisplayName] = useState(userProfile?.displayName || '');
  const [savingName, setSavingName] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [customizationOpen, setCustomizationOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setDisplayName(userProfile?.displayName || '');
  }, [userProfile?.displayName]);

  const handleSaveName = async () => {
    const trimmedName = displayName.trim();
    if (!currentUser?.uid || !trimmedName || trimmedName === userProfile?.displayName) {
      return;
    }

    setSavingName(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        displayName: trimmedName
      });

      if (!currentUser.isChild) {
        await updateAuthProfile(currentUser, { displayName: trimmedName });
      }

      await fetchUserProfile(currentUser.uid);
      onShowToast?.('Name updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating name:', error);
      onShowToast?.(`Failed to update name: ${error.message}`, 'error');
    } finally {
      setSavingName(false);
    }
  };

  const handlePhotoSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!onUploadProfilePhoto) {
      onShowToast?.('Profile photo upload is not available right now.', 'error');
      return;
    }

    setUploadingPhoto(true);
    try {
      await onUploadProfilePhoto(file);
      onShowToast?.('Profile photo updated!', 'success');
    } catch (error) {
      console.error('Profile photo upload failed:', error);
      onShowToast?.(error.message || 'Failed to update profile photo', 'error');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogout = async () => {
    if (!onLogout) return;
    setLogoutLoading(true);
    try {
      await onLogout();
      onShowToast?.('Logged out successfully', 'success');
      onClose?.();
    } catch (error) {
      console.error('Logout failed:', error);
      onShowToast?.('Failed to log out. Please try again.', 'error');
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser?.uid) return;

    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    setDeleting(true);
    try {
      await deleteAccount();
      onShowToast?.('Account deleted successfully', 'success');
      onClose?.();
    } catch (error) {
      console.error('Delete account failed:', error);
      onShowToast?.(`Failed to delete account: ${error.message}`, 'error');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Account & Profile</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box textAlign="center">
              <Avatar
                src={userProfile?.photoURL}
                sx={{
                  width: 96,
                  height: 96,
                  fontSize: 36,
                  bgcolor: '#FF9800',
                  color: 'white',
                  mx: 'auto',
                  mb: 2
                }}
              >
                {!userProfile?.photoURL && userProfile?.displayName?.charAt(0)}
              </Avatar>
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                component="label"
                disabled={uploadingPhoto}
              >
                {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handlePhotoSelect}
                />
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Stored securely at profile-pics/{currentUser?.uid || 'user'}.jpg
              </Typography>
            </Box>

            <Stack spacing={2}>
              <TextField
                label="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Email"
                value={userProfile?.email || 'No email on file'}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <Button
                variant="contained"
                onClick={handleSaveName}
                disabled={savingName || !displayName.trim() || displayName.trim() === userProfile?.displayName}
              >
                {savingName ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>

            <Divider />

            <Stack spacing={1}>
              <Typography variant="subtitle1">Customizations</Typography>
              <Typography variant="body2" color="text.secondary">
                Update your avatar, theme, and background to make Earn-E feel like home.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Palette />}
                onClick={() => setCustomizationOpen(true)}
              >
                Open Visual Customizer
              </Button>
            </Stack>

            <Divider />

            <Stack spacing={2}>
              <Typography variant="subtitle1" color="error">
                Danger Zone
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Permanently delete your account and all associated data.
              </Typography>
              {confirmDelete ? (
                <Alert severity="error">
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    This action cannot be undone.
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      startIcon={<DeleteForever />}
                    >
                      {deleting ? 'Deleting...' : 'Confirm Delete'}
                    </Button>
                    <Button variant="outlined" onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </Button>
                  </Stack>
                </Alert>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForever />}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete Account
                </Button>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Logout />}
            color="warning"
            onClick={handleLogout}
            disabled={logoutLoading}
          >
            {logoutLoading ? 'Logging out...' : 'Logout'}
          </Button>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ProfileCustomization
        open={customizationOpen}
        onClose={() => setCustomizationOpen(false)}
        userProfile={userProfile}
        currentUser={currentUser}
      />
    </>
  );
}

export default UserProfile;
